import { ArrowRight, FileVideo2, Play } from 'lucide-react'
import { useLanguage } from '@afsa/shared'
import { Link, useNavigate } from 'react-router-dom'
import { type WebPageSection } from '@/lib/api'
import { useGalleryHighlights } from '@/lib/api/hooks/gallery'
import { useVideoHighlights } from '@/lib/api/hooks/video'
import { useMemo } from 'react'
import { parseHtmlString } from '@/utils/helpers/html'
import { getVideoPreviewImage } from '@afsa/shared/utils'

interface MediaGalleryProps {
  className?: string
  data: WebPageSection
}

const MediaGallery = ({ className, data }: MediaGalleryProps) => {
  const { t } = useLanguage()
  const navigate = useNavigate()

  // Fetch recent galleries (highlights)
  const { data: galleriesData } = useGalleryHighlights()

  // Fetch videos (highlights) for the large display
  const { data: videosData } = useVideoHighlights()

  // Format date helper
  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear().toString().slice(-2)
    return `${day}/${month}/${year}`
  }

  // Default image constants
  const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755763789/b54f8598c43a335b7dd4b845e6049d21ae14acc9_ac9gp7.jpg"
  // const defaultVideoThumbnail = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png"

      const getPreviewImage = (previewUrl: string | undefined | null) => {
        if (previewUrl) {
            const image = getVideoPreviewImage(previewUrl)
            return image ? (
                <img
                    src={image}
                    alt="Video Preview"
                    className="aspect-video  w-full object-cover"
                
                />
            ) : <FileVideo2 className="w-24 h-24 text-gray-300" />
         }
            return <FileVideo2 className="w-24 h-24 text-gray-300" />

        }

  // Transform galleries data
  const mediaItems = useMemo(() => {
    const galleries = galleriesData?.data || []
    return galleries.map((gallery, index) => ({
      id: gallery.galleryId || gallery.photoGalleryId || `gallery-${index}`,
      image: gallery.photoUrl || gallery.photoDetails?.[0]?.photoUrl || defaultImage,
      title: gallery.title,
      category: gallery.folderName || "Gallery",
      date: formatDate(gallery.datePublished || gallery.createdAt),
      folderName: gallery.folderName
    }))
  }, [galleriesData])

  // Get the main video feature (first highlight)
  const featuredVideo = useMemo(() => {
    if (videosData?.data && videosData.data.length > 0) {
      const video = videosData.data[0];
      return {
        id: video.videoUploadId,
        title: video.title,
        description: video.description,
        // Use custom thumbnailUrl if available, otherwise use default placeholder
        thumbnail: getPreviewImage(video.embeddedLink || video.thumbnailUrl),
        embeddedLink: video.embeddedLink,
        videoUploadId: video.videoUploadId
      }
    }
    // Fallback if no video is Highlighted
    return {
      id: 'inauguration',
      title: t('inauguration_ceremony_title'),
      description: t('inauguration_ceremony_desc'),
      thumbnail: "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760289/Image_ngvhsk.png",
      embeddedLink: null,
      videoUploadId: null
    }
  }, [videosData, t])

  const handlePlayVideo = (embeddedLink?: string | null) => {
    if (embeddedLink) {
      window.open(embeddedLink, '_blank', 'noopener,noreferrer')
    } else {
      navigate('/media/videos')
    }
  }

  return (
    <section className={`py-16 bg-white ${className}`} aria-labelledby="media-gallery-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 xl:grid-cols-[1fr_2fr]  ">

          <div className="flex flex-col gap-6 ">
            <p className="text-sm font-semibold md:font-medium font-satoshi text-wine-100 tracking-wide bg-duskySky-100 inline-block rounded-lg px-2 leading-tight ">
              {t('key_highlight')}
            </p>

            <h2
              id="media-gallery-title"
              className="text-3xl md:text-4xl font-normal font-pt-serif text-gray-900 leading-tight"
            >
              {/* {t('through_lens_afsa')} */}
              {data.title}
            </h2>

              {/* {t('gallery_description')} */}
           {data.description && <div dangerouslySetInnerHTML={parseHtmlString(data.description)}>
            </div>}

            <Link
              to="/media/photo-gallery"
              className="inline-flex w-fit mt-4 items-center px-6 py-3 bg-wine-100 hover:bg-wine-200 text-white font-medium font-satoshi rounded-sm transition-colors duration-200 group"
            >
              {t('learn_more')}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1  transition-transform duration-200 hidden md:block" />
            </Link>
          </div>

          {/* Right Image Grid */}
          <div className="order-2">
            <div className="grid gap-4 xl:grid-cols-2 lg:gap-8">
              {/* Main Featured Item (Video) */}
              <div className="overflow-hidden">
                <div className="relative overflow-hidden group cursor-pointer" onClick={() => handlePlayVideo(featuredVideo.embeddedLink)}>
                  {
                    featuredVideo.thumbnail
                  }
            
                  {/* Play Button Overlay if it's a real video */}
                  {featuredVideo.videoUploadId && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                      <div className="w-12 h-12 bg-wine-100 hover:bg-wine-200 rounded-lg flex items-center justify-center transition-colors duration-300">
                        <Play className="h-6 w-6 text-white fill-current" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <h3 className="text-2xl font-normal font-pt-serif text-gray-900 mb-4">
                    {featuredVideo.title}
                  </h3>
                  <div className=" font-satoshi max-w-4xl mx-auto line-clamp-3" dangerouslySetInnerHTML={parseHtmlString(featuredVideo.description)}>
                  </div>
                </div>
              </div>

              {/* Mobile horizontal scroll */}
              <div className="block lg:hidden mt-8">
                {mediaItems.length > 0 ? (
                  <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide">
                    {mediaItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex-none relative group cursor-pointer overflow-hidden transition-all duration-300 snap-start"
                        onClick={() => navigate(item.folderName ? `/media/photo-gallery/${encodeURIComponent(item.folderName)}` : '/media/photo-gallery')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            navigate(item.folderName ? `/media/photo-gallery/${encodeURIComponent(item.folderName)}` : '/media/photo-gallery')
                          }
                        }}
                        aria-label={`View gallery: ${item.title}`}
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300 aspect-video "
                          onError={(e) => {
                            if (e.currentTarget.src !== defaultImage) {
                              e.currentTarget.src = defaultImage
                            }
                          }}
                        />
                        <p className='text-gray-100 text-sm py-3'>{item.date}</p>
                        <h3 className=" text-lg py-2 font-medium  group-hover:opacity-100 transition-opacity duration-300 delay-100">
                          {item.title}
                        </h3>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No photos available
                  </div>
                )}
              </div>

              {/* Desktop grid */}
              <div className="hidden lg:block h-full">
                {mediaItems.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {mediaItems.map((item) => (
                      <div
                        key={item.id}
                        className="relative group cursor-pointer overflow-hidden transition-all duration-300"
                        onClick={() => navigate(item.folderName ? `/media/photo-gallery/${encodeURIComponent(item.folderName)}` : '/media/photo-gallery')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            navigate(item.folderName ? `/media/photo-gallery/${encodeURIComponent(item.folderName)}` : '/media/photo-gallery')
                          }
                        }}
                        aria-label={`View gallery: ${item.title}`}
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full object-cover group-hover:scale-105 transition-transform duration-300 aspect-video "
                          onError={(e) => {
                            if (e.currentTarget.src !== defaultImage) {
                              e.currentTarget.src = defaultImage
                            }
                          }}
                        />
                        <p className='text-gray-100 text-sm py-3'>{item.date}</p>
                        <h3 className=" text-lg py-2 font-medium  group-hover:opacity-100 transition-opacity duration-300 delay-100">
                          {item.title}
                        </h3>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No photos available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default MediaGallery